import { createAsyncThunk } from '@reduxjs/toolkit';
import { v4 as uuid } from 'uuid';

import { tracker } from 'app-components/utils/core';
import type RootState from 'app-components/utils/redux-store/rootState';

import { undoActions } from '../undo';
import { mkUndo } from '../undo/undoTypes';
import invoiceItemsApi from './invoiceItemsApi';
import { uniqueLowercaseItemNames } from './invoiceItemsSelectors';
import { invoiceItemsActions } from './invoiceItemsSlice';
import type { InvoiceItemType } from './invoiceItemTypes';

type CloneInvoiceItemArgs = {
  item: InvoiceItemType;
  overrides?: Partial<InvoiceItemType>;
};

export const cloneInvoiceItem = createAsyncThunk(
  'invoiceItems/cloneInvoiceItem',
  async ({ item, overrides }: CloneInvoiceItemArgs, thunkApi): Promise<string | null> => {
    let newName = `Copy of ${item.name ?? ''}`;
    const dupNameCheck = uniqueLowercaseItemNames(thunkApi.getState() as RootState);

    if (dupNameCheck.has(newName.toLowerCase())) {
      let counter = 1;
      while (dupNameCheck.has(`${newName} (${counter})`.toLowerCase())) {
        counter++;
      }
      newName = `${newName} (${counter})`;
    }

    const clientId = uuid();
    const clonedItem: InvoiceItemType & { clientId: string } = {
      ...item,
      id: undefined,
      clientId,
      createdAt: undefined,
      modifiedAt: undefined,
      name: newName,
      ...overrides,
    };

    const typeName = clonedItem.itemType === 'PRODUCT' ? 'Product' : 'Service';

    const result = await thunkApi.dispatch(invoiceItemsActions.create(clonedItem)).unwrap();
    const itemId = result?.resource?.id ?? null;

    if (itemId) {
      thunkApi.dispatch(
        undoActions.addUndo(
          mkUndo({
            key: itemId,
            disableUndoAction: true,
            userMessage: `${typeName} successfully cloned`,
          }),
        ),
      );
      return itemId;
    }

    return null;
  },
);

type ArchiveGlobalItemArgs = {
  item: InvoiceItemType;
};

export const archiveGlobalItem = createAsyncThunk(
  'invoiceItems/archiveGlobalItem',
  async ({ item }: ArchiveGlobalItemArgs, thunkApi): Promise<string | null> => {
    const itemId = item.id ?? null;
    if (!itemId) return null;

    await thunkApi.dispatch(invoiceItemsActions.update({ ...item, isArchived: true, id: itemId })).unwrap();

    const typeName = item.itemType === 'PRODUCT' ? 'Product' : 'Service';
    const undoKey = item.id ?? item.clientId ?? item.name ?? 'global-item';

    thunkApi.dispatch(
      undoActions.addUndo(
        mkUndo({
          key: `${undoKey}-archive`,
          disableUndoAction: true,
          userMessage: `${typeName} archived`,
        }),
      ),
    );

    tracker.track(tracker.events.archiveGlobalItem, {
      item_name: item.name ?? 'Unnamed item',
      item_type: typeName,
    });

    return itemId;
  },
);

type UnarchiveGlobalItemArgs = {
  item: InvoiceItemType;
};

export const unarchiveGlobalItem = createAsyncThunk(
  'invoiceItems/unarchiveGlobalItem',
  async ({ item }: UnarchiveGlobalItemArgs, thunkApi): Promise<string | null> => {
    const itemId = item.id ?? null;
    if (!itemId) return null;

    await thunkApi.dispatch(invoiceItemsActions.update({ ...item, isArchived: false, id: itemId })).unwrap();

    const typeName = item.itemType === 'PRODUCT' ? 'Product' : 'Service';
    const undoKey = item.id ?? item.clientId ?? item.name ?? 'global-item';

    thunkApi.dispatch(
      undoActions.addUndo(
        mkUndo({
          key: `${undoKey}-unarchive`,
          disableUndoAction: true,
          userMessage: `${typeName} unarchived`,
        }),
      ),
    );

    tracker.track(tracker.events.unarchiveGlobalItem, {
      item_name: item.name ?? 'Unnamed item',
      item_type: typeName,
    });

    return itemId;
  },
);

export type BatchCreateResult = {
  created: InvoiceItemType[];
  failed: { index: number; name: string; status: string }[];
};

const BATCH_CREATE_SIZE = 100;

export const batchCreateInvoiceItems = createAsyncThunk(
  'invoiceItems/batchCreate',
  async (items: InvoiceItemType[], thunkApi): Promise<BatchCreateResult> => {
    const created: InvoiceItemType[] = [];
    const failed: { index: number; name: string; status: string }[] = [];

    for (let offset = 0; offset < items.length; offset += BATCH_CREATE_SIZE) {
      const chunk = items.slice(offset, offset + BATCH_CREATE_SIZE);

      try {
        const response = await invoiceItemsApi.batchCreate(chunk);

        const chunkCreated: InvoiceItemType[] = [];
        response.items.forEach((item, i) => {
          if (item.code === 201 && item.resource.resource) {
            chunkCreated.push(item.resource.resource);
          } else {
            const isDuplicateName = item.errors?.some((e) => e.extData?.exception?.includes('unique_name'));
            failed.push({
              index: offset + i,
              name: items[offset + i].name ?? '',
              status: isDuplicateName ? 'CONFLICT' : (item.resource.status ?? 'UNKNOWN'),
            });
          }
        });

        if (chunkCreated.length > 0) {
          thunkApi.dispatch(invoiceItemsActions.bumpNudgeInvoiceItems(chunkCreated));
          created.push(...chunkCreated);
        }
      } catch {
        for (let i = 0; i < chunk.length; i++) {
          failed.push({
            index: offset + i,
            name: items[offset + i].name ?? '',
            status: 'ERROR',
          });
        }
      }
    }

    return { created, failed };
  },
);

export type BatchSoftDeleteResult = {
  deleted: number;
  failed: number;
};

export const batchSoftDeleteInvoiceItems = createAsyncThunk(
  'invoiceItems/batchSoftDelete',
  async (items: InvoiceItemType[], thunkApi): Promise<BatchSoftDeleteResult> => {
    let deleted = 0;
    let failed = 0;

    for (let offset = 0; offset < items.length; offset += BATCH_CREATE_SIZE) {
      const chunk = items.slice(offset, offset + BATCH_CREATE_SIZE);

      try {
        // Rename first so the service accepts the name change (it ignores
        // name updates when isDeleted is set in the same call).
        const renameChunk = chunk.map((item) => ({
          ...item,
          name: uuid(),
        }));
        await invoiceItemsApi.batchCreate(renameChunk);

        // Now soft-delete using the already-renamed items.
        const deleteChunk = renameChunk.map((item) => ({
          ...item,
          isDeleted: true,
        }));
        const response = await invoiceItemsApi.batchCreate(deleteChunk);

        const chunkDeleted: InvoiceItemType[] = [];
        response.items.forEach((item) => {
          if (item.resource.resource) {
            chunkDeleted.push(item.resource.resource);
          } else {
            failed++;
          }
        });

        if (chunkDeleted.length > 0) {
          thunkApi.dispatch(invoiceItemsActions.bumpNudgeInvoiceItems(chunkDeleted));
          deleted += chunkDeleted.length;
        }
      } catch {
        failed += chunk.length;
      }
    }

    return { deleted, failed };
  },
);
